import BillTypeApi from "@/Api/BillTypeApi";
import CustomerApi from "@/Api/CustomerApi";
import GoodsApi from "@/Api/GoodsApi";
import GoodsStatusApi from "@/Api/GoodsStatusApi";
import ImportBillApi from "@/Api/ImportBillApi";
import SupplierApi from "@/Api/SupplierApi";
import { IFormItemProps, convertProps } from "@/Common/Form/MyModal";
import { utcStartDay } from "@/Common/date";
import { initOptionsItem } from "@/Common/initOptions";
import { BillTypeCategory } from "@/Models/Enums/BillTypeCategory";
import { ImportBill } from "@/Models/Tables/Bill";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Divider, Form, Input, InputNumber, Modal, Row, Select, Table, message } from "antd";
import dayjs from "dayjs";
import { uniqueId } from "lodash";
import { useCallback, useEffect, useState } from "react";

function Item(props: IFormItemProps<ImportBill>) {
    return <Form.Item {...convertProps(props)}  ></Form.Item>;
}

interface AddFormProps {
    onCancel: () => void;
}

export function AddForm({ onCancel }: AddFormProps) {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [options, setOptions] = useState({
        billType: initOptionsItem(),
        goodsStatus: initOptionsItem(),
        goods: initOptionsItem(),
        customer: initOptionsItem(),
        supplier: initOptionsItem(),
    });
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [billType, goodsStatus, goods, supplier, customer] = await Promise.all([
                    BillTypeApi.getSelectItems(BillTypeCategory.Import),
                    GoodsStatusApi.getSelectItems(),
                    GoodsApi.getSelectItems(),
                    SupplierApi.getSelectItems(),
                    CustomerApi.getSelectItems(),
                ]);
                setOptions({
                    billType, goodsStatus: goodsStatus.filter(s => s.value !== 1), goods: goods.map(s => ({
                        label: (<div>
                            <div>{s.label}</div>
                            <div>{s.standard}</div>
                        </div>), value: s.value, data: s
                    })) as any, supplier, customer
                });
            } catch (error) {
                console.error(error);
            }
            finally {
                setLoading(false);
            }
        })();
    }, [form]);

    const onOk = useCallback(async () => {
        setLoading(true);
        try {
            const d1 = await form.validateFields();
            if ((!d1.notifies) || d1.notifies.length === 0) {
                message.error("明细行不能为空!");
                return;
            }
            d1.notifies.forEach((s: any) => s.productionDate = utcStartDay(s.productionDate));
            const uniqueItems = new Set();
            for (const item of d1.notifies) {
                const identifier = `${item.goodsId}-${item.goodsStatusId}}`;
                if (uniqueItems.has(identifier)) {
                    message.error("不能添加重复的明细行!");
                    return;
                }
                uniqueItems.add(identifier);
            }
            const res = await ImportBillApi.add(d1);
            console.log(res);
            onCancel();
        } catch (error) {
            console.log(error);
        }
        finally {
            setLoading(false);
        }
    }, [form, onCancel])

    return <Modal title="添加入库单" width={1260} open onCancel={onCancel} confirmLoading={loading} onOk={onOk} >
        <Form form={form} layout="vertical">
            <Row gutter={24}>
                <Col span={6}>
                    <Item label="单据类型" name="typeId" required >
                        <Select optionFilterProp="label" options={options.billType} showSearch />
                    </Item>
                </Col>
                <Col span={6}>
                    <Item label="供应商" name="supplierId" >
                        <Select optionFilterProp="label" options={options.supplier} showSearch />
                    </Item>
                </Col>
                <Col span={6}>
                    <Item label="客户" name="customerId"  >
                        <Select optionFilterProp="label" options={options.customer} showSearch />
                    </Item>
                </Col>
                <Col span={6}>
                    <Item label="批次" name="batchNo"  >
                        <Input />
                    </Item>
                </Col>
            </Row>
            <Row>
                <Col span={6}>
                    <Item label="合同号" name="contractNumber"  >
                        <Input />
                    </Item>
                </Col>
            </Row>
            <Divider style={{ marginTop: 0 }} />
            <Form.List name="notifies">
                {(fields, { add, remove }) => (
                    <Table
                        pagination={false}
                        bordered
                        rowKey="key"
                        size="small"
                        scroll={{ y: 240 }}
                        dataSource={fields.map(field => form.getFieldValue(["notifies", field.name]))}
                        columns={[
                            {
                                title: "物品名称", dataIndex: "goodsId", width: 260, render: (v, r, index) => <Form.Item
                                    style={{ margin: 0 }}
                                    name={[index, 'goodsId']}
                                    rules={[{ required: true, message: '请选择商品' }]}
                                >
                                    <Select filterOption={(input, opt: any) => {
                                        return opt.data.label.toLowerCase().includes(input.toLowerCase())
                                            || opt.data.standard?.toLowerCase().includes(input.toLowerCase());
                                    }} showSearch placeholder="请选择商品" options={options.goods} onChange={(s, a) => {
                                        form.setFieldValue(["notifies", index, "standard"], a?.data.standard);
                                        console.log(a?.data)
                                        form.setFieldValue(["notifies", index, "unit"], a?.data.unit);
                                    }} />
                                </Form.Item>
                            },
                            {
                                title: "规格", dataIndex: "standard", width: 120, render: (v, r, index) => <Form.Item
                                    style={{ margin: 0 }}
                                    name={[index, 'standard']}
                                >
                                    <Input disabled className="kedle-input-disabled-color" />
                                </Form.Item>
                            },
                            {
                                title: "生产日期", dataIndex: "productionDate", width: 120, render: (v, r, index) => <Form.Item
                                    style={{ margin: 0 }}
                                    name={[index, 'productionDate']}
                                    rules={[{ required: true, message: '请填写生产日期' }]}
                                    initialValue={dayjs()}
                                >
                                    <DatePicker picker="date" placeholder="生产日期" />
                                </Form.Item>
                            },
                            {
                                title: "品质状态", dataIndex: "goodsStatusId", width: 120, render: (v, r, index) => <Form.Item
                                    style={{ margin: 0 }}
                                    name={[index, 'goodsStatusId']}
                                    rules={[{ required: true, message: '请填写生产日期' }]}
                                    initialValue={2}
                                >
                                    <Select options={options.goodsStatus} optionFilterProp="label" showSearch />
                                </Form.Item>
                            },
                            {
                                title: "数量", dataIndex: "quantity", width: 100, render: (v, r, index) => <Form.Item
                                    style={{ margin: 0 }}
                                    name={[index, 'quantity']} rules={[{ required: true, message: '请输入数量' }]} required
                                >
                                    <InputNumber step={1} precision={2} min={0} placeholder="数量" />
                                </Form.Item>
                            },
                            {
                                title: "单位", dataIndex: "unit", width: 60, render: (v, r, index) => <Form.Item
                                    style={{ margin: 0 }}
                                    name={[index, 'unit']}  >
                                    <Input disabled className="kedle-input-disabled-color" />
                                </Form.Item>
                            },
                            {
                                title: <Button size="small" icon={<PlusOutlined />} onClick={() => {
                                    const maxLineNo = Math.max(...fields.map(field => form.getFieldValue(["notifies", field.name, "fluxNav", "lineNo"])), 0) + 1;
                                    console.log(maxLineNo);
                                    add({ key: uniqueId(), fluxNav: { lineNo: maxLineNo.toString() } }, 0)
                                }} />,
                                key: "action", width: 40, align: "center",
                                render: (v, r, index) => <Button size="small" danger icon={<MinusCircleOutlined />} onClick={() => remove(index)} type="link" />
                            },
                        ]}
                    />
                )}
            </Form.List>
        </Form>
    </Modal>
}
